import React, { useState, useEffect } from "react";
import { AlertCircle, ArrowLeft, CheckCircle2, Coins, Loader2, Lock, Smartphone } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Field } from "@/components/forms";
import { cn } from "@/lib/utils";

interface TipPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { phone: string; network: string; amount: number; message?: string }) => void;
  studentName: string;
  isPending?: boolean;
}

type PaymentState = "input" | "processing" | "success" | "error";

const networks = [
  {
    name: "M-Pesa",
    code: "mpesa",
    prefixes: ["74", "75", "76", "79"],
    logo: "/mnos/m-pesa-seeklogo.png",
  },
  {
    name: "Tigo Pesa",
    code: "mixx",
    prefixes: ["65", "67", "70", "71", "77"],
    logo: "/mnos/mixx-by-yas-seeklogo.png",
  },
  {
    name: "Airtel Money",
    code: "airtel",
    prefixes: ["68", "69", "78"],
    logo: "/mnos/airtel-money-seeklogo.png",
  },
  {
    name: "HaloPesa",
    code: "halotel",
    prefixes: ["61", "62"],
    logo: "/mnos/halopesa-seeklogo.png",
  },
  { name: "T-Pesa", code: "ttcl", prefixes: ["73"], logo: "/mnos/t-pesa-seeklogo.png" },
];

const detectNetwork = (digits: string) => {
  if (digits.length < 2) return null;
  const prefix = digits.substring(0, 2);
  return networks.find((n) => n.prefixes.includes(prefix)) || null;
};

const formatTZS = (amt: number) =>
  new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
  }).format(amt);

const tipPresets = [5000, 10000, 20000, 50000, 100000];

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2 },
};

const TipPaymentModal: React.FC<TipPaymentModalProps> = ({
  open,
  onClose,
  onSubmit,
  studentName,
  isPending = false,
}) => {
  const [phoneDigits, setPhoneDigits] = useState("");
  const [state, setState] = useState<PaymentState>("input");
  const [errorMsg, setErrorMsg] = useState("");
  const [amount, setAmount] = useState<number>(10000);
  const [message, setMessage] = useState("");

  const network = detectNetwork(phoneDigits);
  const isPhoneValid = phoneDigits.length === 9;

  useEffect(() => {
    if (open) {
      setPhoneDigits("");
      setState("input");
      setErrorMsg("");
      setAmount(10000);
      setMessage("");
    }
  }, [open]);

  const handlePay = () => {
    if (!network || !isPhoneValid) return;
    setState("processing");
    onSubmit({ phone: "+255" + phoneDigits, network: network.code, amount, message });
    setTimeout(() => {
      setState("success");
    }, 3500);
  };

  const handleClose = () => {
    setState("input");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && state !== "processing") handleClose();
      }}
    >
      <DialogContent
        className={cn(
          "max-w-md gap-0 p-0",
          // No close affordance while the phone prompt is pending.
          state === "processing" && "[&>button:last-child]:hidden",
        )}
      >
        <div className="flex items-center gap-1.5 px-6 pt-5 pr-12 text-success">
          <Lock className="size-3.5" aria-hidden />
          <span className="eyebrow">Secured by Gateway</span>
        </div>

        <div className="px-6 pt-4 pb-6">
          {state === "input" && (
            <motion.div {...fade} className="space-y-5">
              <div>
                <DialogTitle>Tip {studentName}</DialogTitle>
                <DialogDescription className="mt-1">
                  Send a tip to appreciate their work
                </DialogDescription>
              </div>

              {/* Amount selection */}
              <fieldset>
                <legend className="mb-2 text-sm font-medium text-foreground">Tip Amount</legend>
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {tipPresets.map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-pressed={amount === preset}
                      onClick={() => setAmount(preset)}
                      className={cn(
                        "tabular-nums",
                        amount === preset && "border-foreground bg-accent",
                      )}
                    >
                      {formatTZS(preset)}
                    </Button>
                  ))}
                </div>
                <Slider
                  aria-label="Tip amount"
                  value={[amount]}
                  onValueChange={(v) => setAmount(v[0])}
                  min={1000}
                  max={200000}
                  step={1000}
                />
                <p className="mt-3 text-center font-mono text-lg font-medium tabular-nums text-foreground">
                  {formatTZS(amount)}
                </p>
              </fieldset>

              {/* Message */}
              <Field label="Message (Optional)">
                <Input
                  placeholder="Great work on the project!"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Field>

              {/* Phone input */}
              <Field label="Mobile Number">
                {({ id, describedBy }) => (
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center gap-1.5 border-r border-border pr-2.5 pl-3 text-sm">
                      <Smartphone className="size-4 text-muted-foreground" aria-hidden />
                      <span className="font-medium text-foreground">+255</span>
                    </div>
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      inputMode="numeric"
                      placeholder="7XX XXX XXX"
                      autoFocus
                      value={phoneDigits}
                      onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, "").slice(0, 9))}
                      className="h-11 pr-16 pl-[92px] font-mono tabular-nums"
                    />
                    <div className="absolute inset-y-0 right-1 flex w-14 items-center justify-center overflow-hidden">
                      {network ? (
                        <motion.img
                          key={network.code}
                          initial={{ scale: 0.6, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          src={network.logo}
                          alt={network.name}
                          className="h-6 max-w-12 object-contain"
                        />
                      ) : (
                        <span
                          className="size-6 rounded-full border-2 border-dashed border-border"
                          aria-hidden
                        />
                      )}
                    </div>
                  </div>
                )}
              </Field>

              <Button
                size="lg"
                className="w-full"
                onClick={handlePay}
                disabled={!isPhoneValid || !network || isPending}
              >
                {isPending ? <Loader2 className="animate-spin" /> : <Coins />}
                {isPending
                  ? "Processing..."
                  : network
                    ? `Tip ${formatTZS(amount)}`
                    : "Enter Mobile Number"}
              </Button>
            </motion.div>
          )}

          {state === "processing" && (
            <div className="flex flex-col items-center py-6 text-center" aria-live="polite">
              <Loader2 className="mb-5 size-10 animate-spin text-muted-foreground" aria-hidden />
              <DialogTitle>Confirm on Phone</DialogTitle>
              <DialogDescription className="mt-2 mb-5 px-2">
                We've sent a payment request to{" "}
                <span className="font-medium text-foreground">+255 {phoneDigits}</span>. Please
                enter your mobile money PIN to authorize.
              </DialogDescription>
              <div className="w-full rounded-md border border-warning/30 bg-warning/10 px-4 py-3">
                <p className="text-[13px] font-medium text-warning">
                  Processing via {network?.name}
                </p>
              </div>
            </div>
          )}

          {state === "success" && (
            <motion.div {...fade} className="flex flex-col items-center py-2 text-center">
              <span
                className="mb-5 flex size-12 items-center justify-center rounded-full border border-success/30 bg-success/10 text-success"
                aria-hidden
              >
                <CheckCircle2 className="size-6" />
              </span>
              <DialogTitle>Tip Sent!</DialogTitle>
              <DialogDescription className="mt-2 mb-6">
                You tipped <span className="font-medium text-foreground">{formatTZS(amount)}</span>{" "}
                to <span className="font-medium text-foreground">{studentName}</span>.
                {message && ` "${message}"`}
              </DialogDescription>
              <Button className="w-full" onClick={handleClose}>
                Done
              </Button>
            </motion.div>
          )}

          {state === "error" && (
            <motion.div {...fade} className="flex flex-col items-center py-2 text-center">
              <span
                className="mb-5 flex size-12 items-center justify-center rounded-full border border-destructive/30 bg-destructive/10 text-destructive"
                aria-hidden
              >
                <AlertCircle className="size-6" />
              </span>
              <DialogTitle>Payment Failed</DialogTitle>
              <DialogDescription className="mt-2 mb-6" role="alert">
                {errorMsg}
              </DialogDescription>
              <div className="grid w-full grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setState("input")}>
                  <ArrowLeft />
                  Back
                </Button>
                <Button onClick={() => setState("input")}>Try Again</Button>
              </div>
            </motion.div>
          )}
        </div>

        <div className="border-t border-border bg-card px-6 py-4 text-center">
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Your payment data is encrypted and secure. <br />
            By paying, you agree to our Terms of Service.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TipPaymentModal;
